/**
 * First-party patient analytics for the storefront, patient sign-in and the
 * patient portal. Events go to POST /api/dtp/events, into our own database:
 * they can be tied to a patient, so no third-party SDK ever sees them.
 *
 * - anonymousId: one per browser (localStorage; memory if storage is blocked).
 * - sessionId: one per tab visit (sessionStorage). A new session starts after
 *   30 minutes idle, or when the URL arrives with different campaign params.
 * - Events queue and flush every ~3s, and on tab hide / page unload with
 *   fetch keepalive, so the last page of a visit isn't lost.
 *
 * Privacy (the API re-applies all of it; @bloom/shared holds the rules):
 * pathnames only, never a query string or the #fragment (magic links carry
 * their secret there); landing params are kept but credential/PII values are
 * "[redacted]"; referrers are origin + path; properties are registry keys
 * with primitive values only. Under Global Privacy Control
 * (navigator.globalPrivacyControl) click ids and ad-network params are not
 * captured; first-party product events still are.
 *
 * Never throws. In tests the transport is a no-op unless one is injected.
 */
import {
  ANALYTICS_EVENTS,
  AnalyticsEventName,
  CLICK_ID_KEYS,
  MAX_PROPERTIES,
  MAX_PROPERTY_STRING,
  UTM_KEYS,
  isAnalyticsEventName,
  looksSensitiveValue,
  pathOnly,
  referrerParts,
  sanitizeQueryParams,
} from '@bloom/shared';
import { getClinicSite } from './clinicSite';

export type AnalyticsProperties = Record<string, string | number | boolean | null | undefined>;

interface QueuedEvent {
  name: AnalyticsEventName;
  occurredAt: string;
  path: string;
  properties: Record<string, string | number | boolean>;
}

interface SessionState {
  id: string;
  lastActivity: number;
  /** utm_* and click ids of the URL that started the session. */
  campaignKey: string;
  landing: { path: string; search: string; referrer: string };
}

export type AnalyticsTransport = (request: { url: string; body: string; headers: Record<string, string> }) => Promise<unknown>;

const ANON_KEY = 'bloom-analytics-anon';
const SESSION_KEY = 'bloom-analytics-session';
export const SESSION_TIMEOUT_MS = 30 * 60 * 1000;
export const FLUSH_INTERVAL_MS = 3000;
const MAX_BATCH = 50;
const MAX_QUEUE = 500;

const fetchTransport: AnalyticsTransport = ({ url, body, headers }) =>
  fetch(url, { method: 'POST', keepalive: true, headers, body, credentials: 'omit' });

const state = {
  queue: [] as QueuedEvent[],
  timer: null as ReturnType<typeof setTimeout> | null,
  memoryAnonId: '',
  memorySession: null as SessionState | null,
  listening: false,
  transport: (import.meta.env.MODE === 'test' ? () => Promise.resolve() : fetchTransport) as AnalyticsTransport,
  now: () => Date.now(),
};

/** Tests: swap the transport and clock, and start from a clean queue. */
export function configureAnalytics(options: { transport?: AnalyticsTransport; now?: () => number }): void {
  if (options.transport) state.transport = options.transport;
  if (options.now) state.now = options.now;
}

export function resetAnalyticsForTests(): void {
  if (state.timer) clearTimeout(state.timer);
  state.queue = [];
  state.timer = null;
  state.memoryAnonId = '';
  state.memorySession = null;
}

function randomId(prefix: string): string {
  try {
    const uuid = window.crypto?.randomUUID?.();
    if (uuid) return `${prefix}_${uuid.replace(/-/g, '')}`;
  } catch { /* fall through */ }
  return `${prefix}_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 12)}`;
}

function safeGet(storage: () => Storage, key: string): string | null {
  try { return storage().getItem(key); } catch { return null; }
}
function safeSet(storage: () => Storage, key: string, value: string): boolean {
  try { storage().setItem(key, value); return true; } catch { return false; }
}

const ID_PATTERN = /^[A-Za-z0-9_-]{8,64}$/;

export function getAnonymousId(): string {
  const stored = safeGet(() => window.localStorage, ANON_KEY);
  if (stored && ID_PATTERN.test(stored)) return stored;
  if (state.memoryAnonId) return state.memoryAnonId;
  const id = randomId('a');
  if (!safeSet(() => window.localStorage, ANON_KEY, id)) state.memoryAnonId = id;
  return id;
}

export function globalPrivacyControl(): boolean {
  try { return (navigator as any).globalPrivacyControl === true; } catch { return false; }
}

/** The campaign a URL carries (utm_* and click ids), for "did the campaign change?". */
function campaignKey(search: string): string {
  const params = new URLSearchParams(search);
  const keys = [...UTM_KEYS, ...(globalPrivacyControl() ? [] : CLICK_ID_KEYS)];
  return keys.map(k => (params.get(k) ? `${k}=${params.get(k)}` : '')).filter(Boolean).join('&');
}

function readSession(): SessionState | null {
  const raw = safeGet(() => window.sessionStorage, SESSION_KEY);
  if (raw) {
    try {
      const parsed = JSON.parse(raw) as SessionState;
      if (parsed && ID_PATTERN.test(parsed.id)) return parsed;
    } catch { /* corrupt: start over */ }
  }
  return state.memorySession;
}

function writeSession(session: SessionState): void {
  if (!safeSet(() => window.sessionStorage, SESSION_KEY, JSON.stringify(session))) state.memorySession = session;
}

/**
 * The current session, started or renewed as needed. Its landing context is
 * captured once, when it starts: the URL's query sanitized here (credentials
 * redacted before they leave the browser), the referrer cut to origin + path.
 */
export function getSession(): SessionState {
  const now = state.now();
  const search = typeof window !== 'undefined' ? window.location.search : '';
  const campaign = campaignKey(search);
  let session = readSession();
  const expired = !session || now - session.lastActivity > SESSION_TIMEOUT_MS;
  const newCampaign = !!session && !!campaign && campaign !== session.campaignKey;
  if (!session || expired || newCampaign) {
    const params = sanitizeQueryParams(search, { gpc: globalPrivacyControl() });
    session = {
      id: randomId('s'),
      lastActivity: now,
      campaignKey: campaign,
      landing: {
        path: pathOnly(window.location.pathname),
        search: Object.keys(params).length ? `?${new URLSearchParams(params).toString()}` : '',
        referrer: referrerParts(document.referrer).referrerUrl,
      },
    };
  } else {
    session = { ...session, lastActivity: now };
  }
  writeSession(session);
  return session;
}

/** Registry keys only, primitives only; long or credential-looking strings are dropped. */
function cleanProperties(name: AnalyticsEventName, properties: AnalyticsProperties = {}): QueuedEvent['properties'] {
  const allowed = ANALYTICS_EVENTS[name].properties as readonly string[];
  const out: QueuedEvent['properties'] = {};
  for (const [key, value] of Object.entries(properties)) {
    if (Object.keys(out).length >= MAX_PROPERTIES) break;
    if (!allowed.includes(key)) continue;
    if (typeof value === 'boolean' || (typeof value === 'number' && Number.isFinite(value))) out[key] = value;
    else if (typeof value === 'string' && value.length <= MAX_PROPERTY_STRING && !looksSensitiveValue(value)) out[key] = value;
  }
  return out;
}

/** Where events are sent: same base as dtpApi (a clinic's own domain serves /api/dtp). */
export function analyticsEndpoint(): string {
  if (getClinicSite()) return '/api/dtp/events';
  return `${import.meta.env.VITE_API_URL || '/api'}/dtp/events`;
}

/**
 * Sent with every patient API call so the server's own outcome events
 * (orders, payments, sign-ins) land on the session that produced them.
 */
export function analyticsHeaders(): Record<string, string> {
  try {
    const session = readSession();
    const headers: Record<string, string> = { 'X-Analytics-Anon': getAnonymousId() };
    if (session && state.now() - session.lastActivity <= SESSION_TIMEOUT_MS) headers['X-Analytics-Session'] = session.id;
    return headers;
  } catch {
    return {};
  }
}

function deviceHints() {
  try {
    return {
      screenW: window.screen?.width, screenH: window.screen?.height,
      viewportW: window.innerWidth, viewportH: window.innerHeight,
      dpr: window.devicePixelRatio,
      language: navigator.language,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      touch: (navigator.maxTouchPoints || 0) > 0,
      standalone: window.matchMedia?.('(display-mode: standalone)').matches || (navigator as any).standalone === true,
    };
  } catch {
    return {};
  }
}

/** Send what's queued. `unloading`: the tab is going away, so send everything now. */
export function flush(): Promise<void> {
  try {
    if (state.timer) { clearTimeout(state.timer); state.timer = null; }
    if (!state.queue.length) return Promise.resolve();
    const session = getSession();
    const token = safeGet(() => window.localStorage, 'dtp-token');
    const headers: Record<string, string> = {
      // text/plain is a CORS-simple type: an anonymous flush needs no preflight.
      'Content-Type': 'text/plain;charset=UTF-8',
      ...(token && { Authorization: `Bearer ${token}` }),
    };
    const context = {
      gpc: globalPrivacyControl(),
      landing: session.landing,
      page: { title: document.title?.slice(0, 120), referrer: referrerParts(document.referrer).referrerUrl },
      device: deviceHints(),
    };
    const sends: Promise<unknown>[] = [];
    while (state.queue.length) {
      const events = state.queue.splice(0, MAX_BATCH);
      const body = JSON.stringify({ anonymousId: getAnonymousId(), sessionId: session.id, context, events });
      sends.push(Promise.resolve().then(() => state.transport({ url: analyticsEndpoint(), body, headers })).catch(() => undefined));
    }
    return Promise.all(sends).then(() => undefined);
  } catch {
    return Promise.resolve();
  }
}

function listenForUnload(): void {
  if (state.listening || typeof window === 'undefined') return;
  state.listening = true;
  try {
    document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'hidden') void flush(); });
    window.addEventListener('pagehide', () => { void flush(); });
  } catch { /* no DOM */ }
}

/** Record a product event. Unknown names are ignored (the registry is the contract). */
export function track(name: AnalyticsEventName, properties?: AnalyticsProperties): void {
  try {
    if (!isAnalyticsEventName(name) || ANALYTICS_EVENTS[name].source !== 'client') {
      if (import.meta.env.DEV) console.warn(`[analytics] not a client event: ${String(name)}`);
      return;
    }
    getSession();
    listenForUnload();
    if (state.queue.length >= MAX_QUEUE) state.queue.shift();
    state.queue.push({
      name,
      occurredAt: new Date(state.now()).toISOString(),
      path: pathOnly(window.location.pathname),
      properties: cleanProperties(name, properties),
    });
    if (!state.timer) state.timer = setTimeout(() => { state.timer = null; void flush(); }, FLUSH_INTERVAL_MS);
  } catch {
    // Telemetry never breaks the page.
  }
}

/** Once per item per session (recommendation impressions and the like). */
export function trackOnce(key: string, name: AnalyticsEventName, properties?: AnalyticsProperties): void {
  try {
    const session = getSession();
    const storageKey = `bloom-analytics-once:${session.id}:${key}`;
    if (safeGet(() => window.sessionStorage, storageKey)) return;
    safeSet(() => window.sessionStorage, storageKey, '1');
    track(name, properties);
  } catch { /* never throws */ }
}

/** Patient-facing route trees of the main app; staff pages are never tracked. */
export function isPatientFacingPath(pathname: string): boolean {
  return /^\/(dtp|patient|payment)(\/|$)/.test(pathname);
}
